import { useEffect, useMemo, useRef, useState } from "react";
import { EditorContent } from "@tiptap/react";
import { toRichText } from "@tldraw/tlschema";
import { useDocumentWriting } from "./useDocumentWriting";
import { DocumentFormatting } from "./DocumentFormatting";
import {
  ArrowLeft,
  ArrowUpRight,
  Check,
  Download,
  Link2,
  MessageSquare,
  PanelRight,
  Pencil,
  X,
} from "lucide-react";
import type { BoardDocument } from "../shared/schema";
import {
  createWorkspaceDocument,
  documentLink,
  documentMarkdown,
  downloadDocument,
} from "./workspace-documents";
import type { WorkspaceModel } from "./useWorkspace";

export function DocumentFocus({
  model,
  shape,
  sessionId,
  connected,
  initiallyEdit,
  onClose,
  onOpen,
}: {
  model: WorkspaceModel;
  shape: BoardDocument | null;
  sessionId: string | null;
  connected: boolean;
  initiallyEdit: boolean;
  onClose: () => void;
  onOpen: (id: string) => void;
}) {
  const original = useRef(shape);
  if (shape) original.current = shape;
  const record = shape ?? original.current;
  if (!record) return null;
  return (
    <DocumentFocusBody
      key={record.id}
      model={model}
      shape={shape}
      initial={record}
      sessionId={sessionId}
      connected={connected}
      initiallyEdit={initiallyEdit}
      onClose={onClose}
      onOpen={onOpen}
    />
  );
}

function DocumentFocusBody({
  model,
  shape,
  initial,
  sessionId,
  connected,
  initiallyEdit,
  onClose,
  onOpen,
}: {
  model: WorkspaceModel;
  shape: BoardDocument | null;
  initial: BoardDocument;
  sessionId: string | null;
  connected: boolean;
  initiallyEdit: boolean;
  onClose: () => void;
  onOpen: (id: string) => void;
}) {
  const [beside, setBeside] = useState(false);
  const titleInput = useRef<HTMLTextAreaElement>(null);
  const {
    textEditor,
    title,
    setTitle,
    editing,
    requesting,
    canWrite,
    owner,
    issue,
    draft,
    unresolved,
    requestEdit,
    finishEdit,
  } = useDocumentWriting({
    editor: model.editor,
    roomId: model.room.id,
    userId: model.user.id,
    shape,
    initial,
    sessionId,
    connected,
    canEdit: model.canEdit,
    initiallyEdit,
    titleInput,
  });
  useEffect(() => {
    const resize = () => {
      const field = titleInput.current;
      if (!field) return;
      field.style.height = "auto";
      field.style.height = `${field.scrollHeight}px`;
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [title, beside]);
  function leave() {
    finishEdit();
    onClose();
  }
  function recoveredCopy() {
    const available = draft;
    if (!available || !model.canEdit || !connected) return;
    const id = createWorkspaceDocument(model.editor, undefined, {
      name: `${available.name || "Document"} — recovered`,
      richText: available.richText,
    });
    onOpen(id);
  }
  const references = useMemo(
    () =>
      Array.isArray(shape?.meta.documentReferences)
        ? shape!.meta.documentReferences.filter(
            (id): id is string => typeof id === "string",
          )
        : [],
    [shape?.meta.documentReferences],
  );
  const folio = String(Number(initial.meta.order ?? 0) + 1).padStart(2, "0");
  return (
    <section
      className={`document-focus ${beside ? "beside" : ""}`}
      aria-label="Document editor"
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === "Escape") {
          event.preventDefault();
          if (editing) finishEdit();
          else leave();
        }
      }}
    >
      <header className="document-focus-header">
        <button type="button" onClick={leave} className="document-back">
          <ArrowLeft size={17} strokeWidth={1.5} />
          <span>Canvas</span>
        </button>
        <span className="document-header-rule" />
        <div className="document-project">
          <span>PROJECT</span>
          <strong>{model.room.name}</strong>
        </div>
        <span className="document-focus-state" role="status">
          {!shape
            ? "Document deleted"
            : !connected
              ? draft
                ? "Offline · draft on this device"
                : "Offline · read only"
              : owner && owner !== sessionId
                ? "Another editor is writing"
                : canWrite
                  ? "Writing · live"
                  : requesting
                    ? "Opening…"
                    : "Reading"}
        </span>
        {shape && (
          <div className="document-focus-actions">
            <button
              type="button"
              aria-label="Download Markdown"
              title="Download Markdown"
              onClick={() =>
                downloadDocument(shape, model.editor, model.room.id)
              }
            >
              <Download size={17} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              aria-label="Copy link"
              title="Copy link"
              onClick={() =>
                void navigator.clipboard.writeText(
                  documentLink(model.room.id, shape.id),
                )
              }
            >
              <Link2 size={17} strokeWidth={1.5} />
            </button>
            {model.canComment && (
              <button
                type="button"
                aria-label="Comment on document"
                title="Comment on document"
                onClick={() => {
                  leave();
                  const bounds = model.editor.getShapeGeometry(shape).bounds;
                  model.setPending({ kind: "point", shape, p: bounds.center });
                  model.setMessage(toRichText(""));
                  model.setReview({ mentions: [] });
                  model.setThreadId(null);
                }}
              >
                <MessageSquare size={17} strokeWidth={1.5} />
              </button>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={() => setBeside(!beside)}
          className={`document-beside ${beside ? "active" : ""}`}
          aria-label={beside ? "Focus view" : "Beside canvas"}
          title={beside ? "Focus view" : "Beside canvas"}
        >
          <PanelRight size={17} strokeWidth={1.5} />
        </button>
        {model.canEdit && shape && !shape.isLocked && (
          <button
            type="button"
            disabled={(!!owner && owner !== sessionId) || !connected}
            className="document-edit-action"
            onClick={() => (editing ? finishEdit() : requestEdit())}
          >
            {editing ? <Check size={15} /> : <Pencil size={14} />}
            {editing ? "Done" : "Edit"}
          </button>
        )}
      </header>
      {issue && (
        <p className="document-focus-warning" role="alert">
          {issue}
        </p>
      )}
      {unresolved && (
        <div className="document-focus-warning">
          <span>There is a different draft on this device.</span>
          <button
            type="button"
            disabled={!model.canEdit || !connected}
            onClick={recoveredCopy}
          >
            Open recovered copy
          </button>
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard.writeText(
                documentMarkdown({
                  ...initial,
                  props: {
                    ...initial.props,
                    name: draft!.name,
                    richText: draft!.richText,
                  },
                }),
              );
            }}
          >
            Copy draft
          </button>
        </div>
      )}
      {shape && editing && (
        <div className="document-focus-tools">
          <DocumentFormatting editor={textEditor} disabled={!canWrite} />
        </div>
      )}
      <div className="document-focus-stage">
        <article className="document-focus-page">
          <div className="document-page-masthead">
            <label className="document-title-label" htmlFor="document-title">
              PROJECT DOCUMENT
            </label>
            <span className="document-folio">{folio}</span>
          </div>
          <textarea
            ref={titleInput}
            className="document-name"
            rows={1}
            id="document-title"
            aria-label="Document title"
            maxLength={120}
            value={title}
            readOnly={!canWrite}
            onChange={(event) => {
              const value = event.target.value;
              setTitle(value);
              if (shape && canWrite)
                model.editor.updateShape<BoardDocument>({
                  id: shape.id,
                  type: "document",
                  props: { name: value },
                });
            }}
            onBlur={() => {
              if (!title.trim() && shape && canWrite) {
                setTitle("Untitled document");
                model.editor.updateShape<BoardDocument>({
                  id: shape.id,
                  type: "document",
                  props: { name: "Untitled document" },
                });
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                textEditor?.commands.setTextSelection(1);
                textEditor?.view.focus();
              }
            }}
          />
          <div className="document-title-rule" />
          {(references.length > 0 || (canWrite && model.sheets.length > 0)) && (
            <div className="document-focus-references">
              <span className="document-reference-label">IN THIS PROJECT</span>
              {references.map((id) => {
                const target = model.editor.getShape(id as BoardDocument["id"]);
                return (
                  <span className="document-reference" key={id}>
                    <button
                      type="button"
                      onClick={() => {
                        if (target) {
                          model.editor.select(id as BoardDocument["id"]);
                          model.editor.zoomToBounds(
                            model.editor.getShapePageBounds(target)!,
                            { inset: 160 },
                          );
                          if (!beside) leave();
                        }
                      }}
                    >
                      {target?.type === "sheet"
                        ? target.props.name
                        : "Sheet unavailable"}
                      <ArrowUpRight size={13} strokeWidth={1.5} />
                    </button>
                    {canWrite && shape && (
                      <button
                        type="button"
                        className="document-reference-remove"
                        aria-label={`Remove reference to ${target?.type === "sheet" ? target.props.name : "unavailable sheet"}`}
                        onClick={() =>
                          model.editor.updateShape<BoardDocument>({
                            id: shape.id,
                            type: "document",
                            meta: {
                              ...shape.meta,
                              documentReferences: references.filter(
                                (reference) => reference !== id,
                              ),
                            },
                          })
                        }
                      >
                        <X size={12} strokeWidth={1.5} />
                      </button>
                    )}
                  </span>
                );
              })}
              {canWrite && shape && (
                <select
                  aria-label="Link to sheet"
                  value=""
                  onChange={(event) => {
                    if (!event.target.value) return;
                    const ids = Array.from(
                      new Set([...references, event.target.value]),
                    ).slice(0, 20);
                    model.editor.updateShape<BoardDocument>({
                      id: shape.id,
                      type: "document",
                      meta: { ...shape.meta, documentReferences: ids },
                    });
                  }}
                >
                  <option value="">+ Link a sheet</option>
                  {model.sheets
                    .filter((s) => !references.includes(s.id))
                    .map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.props.name}
                      </option>
                    ))}
                </select>
              )}
            </div>
          )}
          <EditorContent editor={textEditor} className="document-writing" />
          <footer className="document-page-footer">
            <span>{model.room.name}</span>
            <span>DOCUMENT / {folio}</span>
          </footer>
        </article>
      </div>
    </section>
  );
}
